import * as RadixPopover from '@radix-ui/react-popover';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { usePortalStore } from '@air/primitive-portal';

import { WorkspaceAgentPanelImage } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentPanelImage';
import { type ComposerPopoverVirtualAnchor } from '~/components/v2/components/workspace/workspace-agent-panel/composer-host/utils/composerPopoverAnchor';
import { WORKSPACE_AGENT_PANEL_MENTION_ROW_PREVIEW } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentPanelImageDimensions';
import { WorkspaceAgentPlayOverlay } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/cards/WorkspaceAgentPlayOverlay';
import { getWorkspaceAgentPanelImageSrc } from '~/components/v2/components/workspace/workspace-agent-panel/utils/getWorkspaceAgentPanelImageSrc';

/** The editor keeps focus for the whole mention: nothing in here is a place to land. */
const preventAutoFocus = (event: Event) => {
  event.preventDefault();
};

export type WorkspaceAgentMentionImageRowPreviewProps = {
  /** Resolved from the highlighted image row's ref — not `getElementById`. */
  getPreviewAnchorElement: () => HTMLElement | null;
  /**
   * Changes when the mention source or selected context kit changes — not when the highlighted row
   * moves. Row anchors are resolved live each render; this key only resets bind state on tab/kit
   * remounts.
   */
  anchorScopeKey: string;
  /** Timed media: at this size a still is still just a picture unless the card says otherwise. */
  hasPlayOverlay: boolean;
  /**
   * The user pointed at a row. Fetch starts then — overlapping the open delay — not on the list's
   * default first-row highlight, which would hit imgix for a picker nobody has touched.
   */
  isArmed: boolean;
  isOpen: boolean;
  /**
   * While painted this is Radix's topmost layer, so the press never reaches the picker. Dismiss the
   * picker here — the preview is decorative, not an Escape step of its own.
   */
  onEscapeKeyDown: (event: KeyboardEvent) => void;
  /** The row's own still. No still, no preview: there is nothing else this card would draw. */
  thumbnailUrl: string;
};

/**
 * The still the picker shows beside the highlighted image row — asset or brand-kit file — so the
 * user can tell two near-identical saves apart before referencing one. The image and nothing else,
 * matching the canvas composer's own token-chip hover preview: a 180px square on a gray ground, the
 * still letterboxed inside it.
 *
 * Deliberately not `WorkspaceAgentAssetPreviewCard` — that card names, locates and counts its subject,
 * which is a second reading of the row the user is already reading, and it costs two requests per open.
 * The row's `thumbnailUrl` is already on screen, so this draws from it and asks for nothing.
 */
export const WorkspaceAgentMentionImageRowPreview = memo(
  ({
    getPreviewAnchorElement,
    anchorScopeKey,
    hasPlayOverlay,
    isArmed,
    isOpen,
    onEscapeKeyDown,
    thumbnailUrl,
  }: WorkspaceAgentMentionImageRowPreviewProps) => {
    const portal = usePortalStore((state) => state.portal);
    const [hasOpened, setHasOpened] = useState(false);
    const [readySrc, setReadySrc] = useState<string | null>(null);
    const virtualAnchorRef = useRef<ComposerPopoverVirtualAnchor>({
      getBoundingClientRect: () => new DOMRect(),
    });
    const boundAnchorElementRef = useRef<HTMLElement | null>(null);
    // Same URL the `<img>` will use — same dimensions constant, including BYOS signing. A preload of
    // the unsigned or unsized source would not be the request the image makes, and first-open would
    // wait on bytes that never arrive.
    const displaySrc = useMemo(
      () => getWorkspaceAgentPanelImageSrc(thumbnailUrl, WORKSPACE_AGENT_PANEL_MENTION_ROW_PREVIEW),
      [thumbnailUrl],
    );
    const isStillReady = readySrc === displaySrc;

    const liveAnchorElement = isArmed && isOpen ? getPreviewAnchorElement() : null;

    useIsomorphicLayoutEffect(() => {
      setHasOpened(false);
      setReadySrc(null);
    }, [anchorScopeKey]);

    /**
     * Decode off-document so first-open can wait on the still without mounting the `<img>` (that would
     * deadlock: the image is inside the popover, the popover waits on the image). `error` / `complete`
     * count as ready too, or a dead URL would hold the card closed forever.
     */
    useEffect(() => {
      if (!isArmed) {
        setReadySrc(null);

        return;
      }

      let cancelled = false;
      const image = new Image();
      const markReady = () => {
        if (!cancelled) {
          setReadySrc(displaySrc);
        }
      };

      image.addEventListener('load', markReady);
      image.addEventListener('error', markReady);
      image.src = displaySrc;

      if (image.complete) {
        markReady();
      }

      return () => {
        cancelled = true;
        image.removeEventListener('load', markReady);
        image.removeEventListener('error', markReady);
      };
    }, [displaySrc, isArmed]);

    /**
     * First open waits for the still. Once the card has painted, a row change must not close it while
     * the next still catches up — that is a blink. `hasOpened` is what keeps the box up; the `<img>`
     * remounts on `thumbnailUrl` so the previous bitmap is dropped rather than lingering.
     */
    useEffect(() => {
      if (!isOpen) {
        setHasOpened(false);

        return;
      }

      if (isStillReady && liveAnchorElement) {
        setHasOpened(true);
      }
    }, [isOpen, isStillReady, liveAnchorElement]);

    const isPainted = isArmed && isOpen && liveAnchorElement != null && (hasOpened || isStillReady);

    if (!isPainted) {
      boundAnchorElementRef.current = null;

      return null;
    }

    // Radix reads the virtual anchor in a child effect on the same commit — refs must be current before paint.
    boundAnchorElementRef.current = liveAnchorElement;
    virtualAnchorRef.current = {
      getBoundingClientRect: () => boundAnchorElementRef.current?.getBoundingClientRect() ?? new DOMRect(),
    };

    return (
      <RadixPopover.Root key={anchorScopeKey} open>
        {/* `virtualRef` takes any `getBoundingClientRect` — here the highlighted row's own element. */}
        <RadixPopover.Anchor virtualRef={virtualAnchorRef} />
        <RadixPopover.Portal container={portal}>
          <RadixPopover.Content
            align="start"
            /**
             * Decorative: the row it hangs off already announces the subject by name, and this adds a
             * picture of it — nothing a reader can act on, and nothing in here takes focus.
             */
            aria-hidden
            /**
             * Inert: the preview describes the row rather than offering anything of its own, and
             * `pointer-events-none` also keeps it from eating the pointer on its way back to a row.
             */
            className="pointer-events-none z-portal overflow-hidden rounded-8 border ads:border-ads-gray-5 border-gray-5 shadow-popover"
            collisionPadding={8}
            data-testid="WORKSPACE_AGENT_MENTION_ROW_PREVIEW"
            onCloseAutoFocus={preventAutoFocus}
            onEscapeKeyDown={onEscapeKeyDown}
            onOpenAutoFocus={preventAutoFocus}
            // Radix hardcodes `dialog`, which would announce a picture nobody can enter as one.
            role="presentation"
            // Beside the picker, never over it: the row the preview is about has to stay visible.
            side="left"
            sideOffset={8}
          >
            {/* Bracketed, and square: the canvas composer's own hover-preview box, which no token lands on. */}
            <span className="flex size-[180px] items-center justify-center ads:bg-subtle bg-gray-3 p-1">
              <span className="relative flex size-full items-center justify-center">
                <WorkspaceAgentPanelImage
                  {...WORKSPACE_AGENT_PANEL_MENTION_ROW_PREVIEW}
                  alt=""
                  // Letterboxed, never cropped: a crop would hide the part the user is telling saves apart by.
                  className="max-h-full max-w-full object-contain"
                  data-testid="WORKSPACE_AGENT_MENTION_ROW_PREVIEW_IMAGE"
                  key={thumbnailUrl}
                  src={thumbnailUrl}
                />
                {hasPlayOverlay ? (
                  <WorkspaceAgentPlayOverlay testId="WORKSPACE_AGENT_MENTION_ROW_PREVIEW_PLAY" />
                ) : null}
              </span>
            </span>
          </RadixPopover.Content>
        </RadixPopover.Portal>
      </RadixPopover.Root>
    );
  },
);

WorkspaceAgentMentionImageRowPreview.displayName = 'WorkspaceAgentMentionImageRowPreview';
